import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import GearMark from "@/components/GearMark";
import { APPS, BOOKING_URL, NAV_LINKS } from "@/config";

function LoginMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="nav-login-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-inksoft transition-colors duration-300 hover:text-ink"
      >
        Log in
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-full mt-4 w-72 overflow-hidden rounded-2xl border border-line bg-paper/95 shadow-[0_24px_60px_-30px_rgba(31,28,25,0.4)] backdrop-blur-xl"
            data-testid="nav-login-menu"
          >
            {APPS.map((a) => (
              <a
                key={a.id}
                data-testid={`nav-login-${a.id}-btn`}
                href={a.loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-3 border-b border-line px-5 py-4 transition-colors last:border-0 hover:bg-parchment"
              >
                <span>
                  <span className="block font-display text-sm font-bold text-ink">{a.shortName}</span>
                  <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.18em] text-inksoft">
                    {a.loginLabel}
                  </span>
                </span>
                <ArrowUpRight
                  size={15}
                  className="shrink-0 text-vermillion transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SolutionsMenu() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = pathname === "/solutions" || APPS.some((a) => a.path === pathname);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to="/solutions"
        data-testid="nav-solutions-toggle-link"
        className={`relative flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
          active ? "text-vermillion" : "text-inksoft hover:text-ink"
        }`}
      >
        Solutions
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
        <span
          className={`absolute -bottom-1.5 left-0 h-px bg-vermillion transition-all duration-300 ${
            active ? "w-full" : "w-0"
          }`}
        />
      </NavLink>
      <div className={`absolute left-0 top-full z-10 pt-4 ${open ? "" : "pointer-events-none"}`}>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="w-80 overflow-hidden rounded-2xl border border-line bg-paper/95 shadow-[0_24px_60px_-30px_rgba(31,28,25,0.4)] backdrop-blur-xl"
              data-testid="nav-solutions-menu"
            >
              {APPS.map((a) => {
                const isCurrent = pathname === a.path;
                return (
                  <Link
                    key={a.id}
                    to={a.path}
                    data-testid={`nav-solutions-${a.id}-link`}
                    className={`group flex items-center justify-between gap-3 border-b border-line px-5 py-4 transition-colors last:border-0 ${
                      isCurrent ? "bg-vermillion-tint/70" : "hover:bg-parchment"
                    }`}
                  >
                    <span>
                      <span
                        className={`flex items-center gap-2 font-display text-sm font-bold ${
                          isCurrent ? "text-vermillion" : "text-ink"
                        }`}
                      >
                        {isCurrent ? <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" /> : null}
                        {a.shortName}
                      </span>
                      <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.18em] text-inksoft">
                        {isCurrent ? "You're here" : a.loginLabel}
                      </span>
                    </span>
                    <ArrowUpRight
                      size={15}
                      className="shrink-0 text-vermillion transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </Link>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-paper/80 backdrop-blur-xl">
      <div
        className={`pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-bluerule/60 to-transparent transition-opacity duration-300 ${
          scrolled ? "opacity-100" : "opacity-0"
        }`}
      />
      <div className="container-t flex h-[72px] items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="group flex items-center gap-2.5"
          aria-label="Transactr home"
        >
          <GearMark
            size={30}
            className="transition-transform duration-700 ease-out group-hover:rotate-[135deg]"
          />
          <span className="font-display text-[1.3rem] font-extrabold tracking-[-0.02em] text-ink">
            Transactr
          </span>
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) =>
            l.label === "Solutions" ? (
              <SolutionsMenu key={l.to} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-vermillion" : "text-inksoft hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px bg-vermillion transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ),
          )}
          <LoginMenu />
          <a
            data-testid="nav-cta-get-a-demo-btn"
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold tracking-wide text-paper transition-all duration-300 hover:bg-vermillion active:scale-[0.98]"
          >
            Get a demo
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-paper text-ink transition-colors hover:border-ink md:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line bg-paper/95 backdrop-blur-xl md:hidden"
          >
            <div className="container-t flex flex-col py-4">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-${l.testid}`}
                    className="flex items-center justify-between border-b border-line py-4 font-display text-lg font-semibold text-ink"
                  >
                    {l.label}
                    <ArrowRight size={16} className="text-vermillion" />
                  </Link>
                </motion.div>
              ))}
              <div className="mt-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-inksoft">Log in</p>
                <div className="mt-1">
                  {APPS.map((a) => (
                    <a
                      key={a.id}
                      data-testid={`nav-mobile-login-${a.id}-btn`}
                      href={a.loginUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between border-b border-line py-3.5 font-display text-base font-semibold text-ink"
                    >
                      {a.shortName}
                      <ArrowUpRight size={15} className="text-vermillion" />
                    </a>
                  ))}
                </div>
              </div>
              <a
                data-testid="nav-mobile-cta-btn"
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-vermillion px-6 py-3.5 text-sm font-semibold text-white"
              >
                Get a demo
                <ArrowRight size={15} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
